// app/src/lib/legal/disclaimer.js — the "this is not accounting software"
// acknowledgment shown before an account is created.
//
// Kanjo reads bank feeds and scanned receipts to tell an operator how the week
// is going. It is an OPERATING tool: figures are approximations by design
// (docs/precision: ~2% on sales, ~5% on cashflow is fine, because timeliness
// beats precision for the decision it supports). Books built this way must not
// stand in for accounting records, financial statements, or a tax return — so
// the person creating the account has to see that and acknowledge it BEFORE
// there is an account.
//
// Storage is two-stage on purpose. Signup redirects to Cognito's hosted UI, so
// at the moment of acknowledgment there is no user to attach it to: it goes to
// this device first, then gets stamped into the user's profile on the first
// authenticated boot, where it rides along with the book.
//
// Bump VERSION when the wording changes materially — a prior acceptance no
// longer counts and the operator is asked again.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';

export const VERSION = 1;

// 'disclaimerAccepted' is declared in scripts/lint-no-localstorage.mjs's
// allow-list. The key is written as a LITERAL on purpose: hiding it behind a
// constant would slip past that lint, and the point of the lint is that every
// localStorage key in this library is a deliberate, reviewed decision.
// Reads/writes are wrapped because private mode throws on access.

/** Record acceptance on THIS DEVICE (pre-auth). Safe to call repeatedly. */
export function acceptOnDevice(now = new Date()) {
  const record = { version: VERSION, at: now.toISOString() };
  try { window.localStorage.setItem('disclaimerAccepted', JSON.stringify(record)); }
  catch { /* private mode — the gate will simply ask again */ }
  return record;
}

/** The device's acceptance record, or null when absent/older/unreadable. */
export function deviceAcceptance() {
  try {
    const raw = window.localStorage.getItem('disclaimerAccepted');
    if (!raw) return null;
    const rec = JSON.parse(raw);
    return rec && rec.version === VERSION && rec.at ? rec : null;
  } catch { return null; }
}

/**
 * Does this device still need to see the disclaimer before signing up?
 * Unreadable storage returns true — asking twice is a nuisance, skipping the
 * warning is the failure that matters.
 */
export function needsAcknowledgment() {
  return !deviceAcceptance();
}

/**
 * Copy the device's acceptance into the signed-in user's profile, so the record
 * lives with the book rather than only in a browser store the operator can
 * clear. No-op when there's nothing to copy or the profile already has this
 * version. Best-effort: never throws into the boot path.
 * @returns {Promise<boolean>} true when the profile was written
 */
export async function stampProfile() {
  try {
    const rec = deviceAcceptance();
    if (!rec) return false;
    const claims = await currentClaims();
    if (!claims) return false;
    const existing = (await db.userProfile.get(claims.sub)) || {};
    if (existing.disclaimerVersion === VERSION) return false;
    await db.userProfile.put({
      ...existing,
      userId: claims.sub,
      disclaimerVersion: VERSION,
      disclaimerAcceptedAt: rec.at,
    });
    return true;
  } catch { return false; }
}
